import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Send, MessageCircle, Loader2, User, Shield, Camera, Image, X, Video, Square, Play, Languages, Mic, MicOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatPanel({ requestId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [mediaUrls, setMediaUrls] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraMode, setCameraMode] = useState('photo'); // photo or video
  const [translations, setTranslations] = useState({}); // msgId -> translated text
  const [translatingId, setTranslatingId] = useState(null);
  const [targetLang, setTargetLang] = useState('it');
  const [isRecordingAudio, setIsRecordingAudio] = useState(false);
  const [audioRecordingTime, setAudioRecordingTime] = useState(0);
  const audioRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioStreamRef = useRef(null);
  const audioTimerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };
  const isOperator = user && (user.role === 'admin' || user.role === 'operatore');

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/requests/${requestId}/messages`, { headers });
      setMessages(data.messages || []);
      await axios.put(`${API}/requests/${requestId}/messages/read`, {}, { headers }).catch(() => {});
      for (const msg of (data.messages || [])) {
        if ((msg.tipo === 'foto' || msg.tipo === 'video' || msg.tipo === 'audio') && msg.foto_id && !mediaUrls[msg.foto_id]) {
          try {
            const resp = await axios.get(`${API}/files/${msg.foto_id}/download`, { headers, responseType: 'blob' });
            setMediaUrls(prev => ({ ...prev, [msg.foto_id]: URL.createObjectURL(resp.data) }));
          } catch { /* skip */ }
        }
      }
    } catch (e) {
      console.error('Chat fetch error:', e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 3000);
    return () => { clearInterval(pollRef.current); stopCamera(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (tipo = 'testo') => {
    if (!newMsg.trim() && tipo === 'testo') return;
    setSending(true);
    try {
      const msgText = tipo === 'richiesta_foto' ? 'Richiesta foto dalla fotocamera'
        : tipo === 'richiesta_video' ? 'Richiesta video in tempo reale'
        : newMsg.trim();
      await axios.post(`${API}/requests/${requestId}/messages`, { messaggio: msgText, tipo }, { headers });
      setNewMsg('');
      fetchMessages();
    } catch (e) { console.error('Send error:', e); }
    finally { setSending(false); }
  };

  const sendPhotoFile = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/requests/${requestId}/messages/photo`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchMessages();
      toast.success('Foto inviata');
    } catch { toast.error('Errore invio foto'); }
    finally { setSending(false); }
  };

  const sendVideoFile = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/requests/${requestId}/messages/video`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchMessages();
      toast.success('Video inviato');
    } catch { toast.error('Errore invio video'); }
    finally { setSending(false); }
  };

  // Camera
  const startCamera = async (mode = 'photo') => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: mode === 'video'
      });
      streamRef.current = stream;
      setCameraMode(mode);
      setShowCamera(true);
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream; }, 100);
    } catch { toast.error('Impossibile accedere alla fotocamera'); }
  };

  const stopCamera = () => {
    stopRecording();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
    setIsRecording(false);
    setRecordingTime(0);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);
    c.toBlob(async (blob) => {
      if (blob) {
        await sendPhotoFile(new File([blob], `foto_${Date.now()}.jpg`, { type: 'image/jpeg' }));
        stopCamera();
      }
    }, 'image/jpeg', 0.85);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'video/mp4';
    try {
      const recorder = new MediaRecorder(streamRef.current, { mimeType });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const ext = mimeType.includes('webm') ? 'webm' : 'mp4';
        await sendVideoFile(new File([blob], `video_${Date.now()}.${ext}`, { type: mimeType }));
        stopCamera();
      };
      recorder.start(1000);
      recorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
    } catch { toast.error('Registrazione non supportata'); }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
    setIsRecording(false);
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, '0')}:${(s % 60).toString().padStart(2, '0')}`;

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  // --- Voice notes (audio recording + auto-transcription via Whisper) ---
  const startAudioRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm'
        : 'audio/mp4';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(t => t.stop());
          audioStreamRef.current = null;
        }
        await sendVoiceFile(new File([blob], `voice_${Date.now()}.webm`, { type: mimeType }));
      };
      recorder.start(1000);
      audioRecorderRef.current = recorder;
      setIsRecordingAudio(true);
      setAudioRecordingTime(0);
      audioTimerRef.current = setInterval(() => setAudioRecordingTime(t => t + 1), 1000);
    } catch {
      toast.error('Impossibile accedere al microfono');
    }
  };

  const stopAudioRecording = () => {
    if (audioTimerRef.current) { clearInterval(audioTimerRef.current); audioTimerRef.current = null; }
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      audioRecorderRef.current.stop();
      audioRecorderRef.current = null;
    }
    setIsRecordingAudio(false);
  };

  const cancelAudioRecording = () => {
    audioChunksRef.current = [];
    if (audioTimerRef.current) { clearInterval(audioTimerRef.current); audioTimerRef.current = null; }
    if (audioRecorderRef.current && audioRecorderRef.current.state !== 'inactive') {
      audioRecorderRef.current.onstop = null;
      audioRecorderRef.current.stop();
      audioRecorderRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
    setIsRecordingAudio(false);
    setAudioRecordingTime(0);
  };

  const sendVoiceFile = async (file) => {
    if (!file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/requests/${requestId}/messages/voice`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      fetchMessages();
      toast.success('Nota vocale inviata con trascrizione');
    } catch { toast.error('Errore invio nota vocale'); }
    finally {
      setSending(false);
      setAudioRecordingTime(0);
    }
  };


  const translateMessage = async (msg) => {
    setTranslatingId(msg.id);
    try {
      const { data } = await axios.post(`${API}/translate`, {
        testo: msg.messaggio, lingua_destinazione: targetLang,
      }, { headers });
      setTranslations(prev => ({ ...prev, [msg.id]: data.traduzione }));
      if (data.error) toast.error(data.error);
    } catch (e) {
      toast.error('Traduzione fallita');
    } finally { setTranslatingId(null); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-panel">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-[#B8D4E8] bg-white">
        <MessageCircle size={16} className="text-[#FF3B30]" />
        <span className="text-xs font-bold uppercase tracking-widest text-[#0D1B2A]">Chat in tempo reale</span>
        <div className="flex items-center gap-2 ml-auto">
          <div className="flex items-center gap-1" title="Lingua per traduzione AI">
            <Languages size={12} className="text-[#4A6B8A]" />
            <select
              value={targetLang}
              onChange={(e) => setTargetLang(e.target.value)}
              className="bg-white border border-[#B8D4E8] text-[#0D1B2A] px-1 py-0.5 text-[10px] uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
              data-testid="chat-translate-lang"
            >
              <option value="it">IT</option>
              <option value="en">EN</option>
              <option value="fr">FR</option>
              <option value="es">ES</option>
              <option value="zh">ZH</option>
              <option value="ru">RU</option>
              <option value="ar">AR</option>
            </select>
          </div>
          <div className="w-2 h-2 bg-[#32D74B] rounded-full animate-pulse" />
          <span className="text-[9px] text-[#32D74B] font-mono uppercase tracking-widest">Live</span>
        </div>
      </div>

      {/* Camera/Video view */}
      {showCamera && (
        <div className="relative bg-black" data-testid="camera-view">
          <video ref={videoRef} autoPlay playsInline muted className="w-full h-56 object-cover" />
          <canvas ref={canvasRef} className="hidden" />
          {isRecording && (
            <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1 bg-[#FF3B30] text-white">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              <span className="text-xs font-mono font-bold">{formatTime(recordingTime)}</span>
              <span className="text-[10px] uppercase">REC</span>
            </div>
          )}
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
            {cameraMode === 'photo' ? (
              <button onClick={capturePhoto} className="px-6 py-2 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest" data-testid="capture-photo-btn">
                <Camera size={14} className="inline mr-1" /> Scatta Foto
              </button>
            ) : (
              <>
                {!isRecording ? (
                  <button onClick={startRecording} className="px-6 py-2 bg-[#FF3B30] text-white text-xs font-bold uppercase tracking-widest animate-pulse" data-testid="start-recording-btn">
                    <Video size={14} className="inline mr-1" /> Avvia Registrazione
                  </button>
                ) : (
                  <button onClick={stopRecording} className="px-6 py-2 bg-white text-[#FF3B30] text-xs font-bold uppercase tracking-widest border-2 border-[#FF3B30]" data-testid="stop-recording-btn">
                    <Square size={14} className="inline mr-1" /> Stop e Invia ({formatTime(recordingTime)})
                  </button>
                )}
              </>
            )}
            <button onClick={stopCamera} className="px-4 py-2 bg-black/60 text-white text-xs uppercase tracking-widest border border-white/20" data-testid="close-camera-btn">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F0F7FC] min-h-[200px] max-h-[400px]" data-testid="chat-messages">
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 size={20} className="animate-spin text-[#4A6B8A]" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle size={32} className="mx-auto text-[#B8D4E8] mb-2" />
            <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">
              {isOperator ? 'Scrivi un messaggio per attivare la chat' : 'Nessun messaggio ancora'}
            </p>
          </div>
        ) : (
          messages.map((msg) => {
            const isMine = msg.sender_id === user?._id;
            return (
              <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${msg.id}`}>
                <div className="max-w-[80%]">
                  {/* Sender */}
                  <div className={`flex items-center gap-1 mb-1 ${isMine ? 'justify-end' : ''}`}>
                    {msg.is_operator ? <Shield size={10} className="text-[#FF3B30]" /> : <User size={10} className="text-[#4A6B8A]" />}
                    <span className="text-[9px] uppercase tracking-widest font-bold" style={{ color: msg.is_operator ? '#FF3B30' : '#4A6B8A' }}>{msg.sender_name || 'Utente'}</span>
                    <span className="text-[8px] text-[#B8D4E8] ml-1">{new Date(msg.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>

                  {/* Camera request */}
                  {msg.tipo === 'richiesta_foto' ? (
                    <div className={`px-3 py-3 border-2 border-dashed ${isMine ? 'border-[#FF3B30]/30 bg-[#FF3B30]/5' : 'border-[#0A84FF]/30 bg-[#0A84FF]/5'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Camera size={16} className="text-[#FF3B30]" />
                        <span className="text-xs font-bold text-[#FF3B30] uppercase">Richiesta Foto</span>
                      </div>
                      <p className="text-xs text-[#4A6B8A] mb-2">L&apos;operatore ha richiesto una foto</p>
                      {!isOperator && (
                        <div className="flex gap-2">
                          <button onClick={() => startCamera('photo')} className="flex items-center gap-1 px-3 py-1.5 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest" data-testid="open-camera-btn"><Camera size={12} /> Fotocamera</button>
                          <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1 px-3 py-1.5 border border-[#B8D4E8] text-[#4A6B8A] text-[10px] font-bold uppercase tracking-widest" data-testid="upload-photo-btn"><Image size={12} /> Galleria</button>
                        </div>
                      )}
                    </div>
                  ) : msg.tipo === 'richiesta_video' ? (
                    /* Video request */
                    <div className={`px-3 py-3 border-2 border-dashed ${isMine ? 'border-[#FF3B30]/30 bg-[#FF3B30]/5' : 'border-[#0A84FF]/30 bg-[#0A84FF]/5'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Video size={16} className="text-[#FF3B30]" />
                        <span className="text-xs font-bold text-[#FF3B30] uppercase">Richiesta Video</span>
                      </div>
                      <p className="text-xs text-[#4A6B8A] mb-2">L&apos;operatore ha richiesto un video in tempo reale</p>
                      {!isOperator && (
                        <div className="flex gap-2">
                          <button onClick={() => startCamera('video')} className="flex items-center gap-1 px-3 py-1.5 bg-[#FF3B30] text-white text-[10px] font-bold uppercase tracking-widest" data-testid="open-video-btn"><Video size={12} /> Avvia Video</button>
                          <button onClick={() => videoInputRef.current?.click()} className="flex items-center gap-1 px-3 py-1.5 border border-[#B8D4E8] text-[#4A6B8A] text-[10px] font-bold uppercase tracking-widest" data-testid="upload-video-btn"><Play size={12} /> Carica Video</button>
                        </div>
                      )}
                    </div>
                  ) : msg.tipo === 'foto' && msg.foto_id ? (
                    /* Photo */
                    <div className={`${isMine ? (msg.is_operator ? 'bg-[#FF3B30]' : 'bg-[#0A84FF]') : 'bg-white border border-[#B8D4E8]'} p-1`}>
                      {mediaUrls[msg.foto_id] ? (
                        <img src={mediaUrls[msg.foto_id]} alt="Foto" className="max-w-full max-h-48 cursor-pointer" onClick={() => window.open(mediaUrls[msg.foto_id], '_blank')} />
                      ) : (
                        <div className="w-full h-24 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#B8D4E8]" /></div>
                      )}
                      <p className={`text-[10px] mt-1 px-1 ${isMine ? 'text-white/70' : 'text-[#6B8AB5]'}`}>Foto</p>
                    </div>
                  ) : msg.tipo === 'video' && msg.foto_id ? (
                    /* Video */
                    <div className={`${isMine ? (msg.is_operator ? 'bg-[#FF3B30]' : 'bg-[#0A84FF]') : 'bg-white border border-[#B8D4E8]'} p-1`}>
                      {mediaUrls[msg.foto_id] ? (
                        <video src={mediaUrls[msg.foto_id]} controls className="max-w-full max-h-48" data-testid={`video-player-${msg.id}`} />
                      ) : (
                        <div className="w-full h-24 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#B8D4E8]" /></div>
                      )}
                      <p className={`text-[10px] mt-1 px-1 flex items-center gap-1 ${isMine ? 'text-white/70' : 'text-[#6B8AB5]'}`}><Video size={10} /> Video</p>
                    </div>
                  ) : msg.tipo === 'audio' && msg.foto_id ? (
                    /* Voice note with transcription */
                    <div className={`p-2 min-w-[220px] ${isMine ? (msg.is_operator ? 'bg-[#FF3B30] text-white' : 'bg-[#0A84FF] text-white') : 'bg-white border border-[#B8D4E8] text-[#0D1B2A]'}`} data-testid={`voice-msg-${msg.id}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <Mic size={12} className={isMine ? 'text-white' : 'text-[#FF3B30]'} />
                        <span className="text-[10px] font-bold uppercase tracking-widest">Nota vocale</span>
                      </div>
                      {mediaUrls[msg.foto_id] ? (
                        <audio src={mediaUrls[msg.foto_id]} controls className="w-full h-8" data-testid={`audio-player-${msg.id}`} />
                      ) : (
                        <div className="flex items-center gap-2 h-8 text-xs"><Loader2 size={12} className="animate-spin" /> caricamento...</div>
                      )}
                      {msg.trascrizione && (
                        <p className={`mt-1 text-xs italic leading-snug ${isMine ? 'text-white/95' : 'text-[#4A6B8A]'}`}>
                          {`"${msg.trascrizione}"`}
                        </p>
                      )}
                    </div>
                  ) : (
                    /* Text */
                    <div>
                      <div className={`px-3 py-2 text-sm leading-relaxed ${
                        isMine ? (msg.is_operator ? 'bg-[#FF3B30] text-white' : 'bg-[#0A84FF] text-white')
                          : (msg.is_operator ? 'bg-white border border-[#FF3B30]/20 text-[#0D1B2A]' : 'bg-white border border-[#B8D4E8] text-[#0D1B2A]')
                      }`}>{msg.messaggio}</div>
                      {translations[msg.id] && (
                        <div className={`mt-1 px-3 py-2 text-xs italic leading-relaxed border-l-2 border-[#0A84FF] ${isMine ? 'bg-white/90 text-[#0D1B2A]' : 'bg-[#0A84FF]/5 text-[#0D1B2A]'}`} data-testid={`translation-${msg.id}`}>
                          <Languages size={10} className="inline mr-1 text-[#0A84FF]" />
                          {translations[msg.id]}
                        </div>
                      )}
                      {!translations[msg.id] && msg.messaggio && (
                        <button
                          onClick={() => translateMessage(msg)}
                          disabled={translatingId === msg.id}
                          className={`mt-1 flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold ${isMine ? 'text-white/70 hover:text-white ml-auto' : 'text-[#0A84FF] hover:text-[#0070E0]'}`}
                          data-testid={`translate-btn-${msg.id}`}
                        >
                          {translatingId === msg.id ? <Loader2 size={9} className="animate-spin" /> : <Languages size={9} />}
                          Traduci ({targetLang.toUpperCase()})
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-[#B8D4E8] bg-white">
        <div className="flex gap-2">
          {isOperator && (
            <>
              <button onClick={() => sendMessage('richiesta_foto')} disabled={sending}
                className="px-2 py-2 bg-[#0A84FF] text-white hover:bg-[#0070E0] transition-all disabled:opacity-40" title="Richiedi foto" data-testid="request-camera-btn">
                <Camera size={16} />
              </button>
              <button onClick={() => sendMessage('richiesta_video')} disabled={sending}
                className="px-2 py-2 bg-[#FF9F0A] text-white hover:bg-[#E68F00] transition-all disabled:opacity-40" title="Richiedi video" data-testid="request-video-btn">
                <Video size={16} />
              </button>
            </>
          )}
          <button onClick={() => fileInputRef.current?.click()} disabled={sending}
            className="px-2 py-2 border border-[#B8D4E8] text-[#4A6B8A] hover:border-[#FF3B30] hover:text-[#FF3B30] transition-all disabled:opacity-40" title="Invia foto" data-testid="send-photo-btn">
            <Image size={16} />
          </button>
          <button onClick={() => videoInputRef.current?.click()} disabled={sending}
            className="px-2 py-2 border border-[#B8D4E8] text-[#4A6B8A] hover:border-[#FF9F0A] hover:text-[#FF9F0A] transition-all disabled:opacity-40" title="Invia video" data-testid="send-video-btn">
            <Video size={16} />
          </button>
          {!isRecordingAudio ? (
            <button onClick={startAudioRecording} disabled={sending}
              className="px-2 py-2 border border-[#B8D4E8] text-[#4A6B8A] hover:border-[#32D74B] hover:text-[#32D74B] transition-all disabled:opacity-40" title="Registra nota vocale" data-testid="start-voice-btn">
              <Mic size={16} />
            </button>
          ) : (
            <div className="flex items-center gap-1 px-2 py-1 bg-[#FF3B30]/10 border border-[#FF3B30]">
              <div className="w-1.5 h-1.5 bg-[#FF3B30] rounded-full animate-pulse" />
              <span className="text-[10px] font-mono font-bold text-[#FF3B30]" data-testid="voice-timer">{formatTime(audioRecordingTime)}</span>
              <button onClick={stopAudioRecording} className="text-[#FF3B30] hover:text-[#FF1A1A] px-1" title="Stop e invia" data-testid="stop-voice-btn">
                <Square size={14} />
              </button>
              <button onClick={cancelAudioRecording} className="text-[#6B8AB5] hover:text-[#FF3B30] px-1" title="Annulla" data-testid="cancel-voice-btn">
                <MicOff size={14} />
              </button>
            </div>
          )}
          <textarea value={newMsg} onChange={(e) => setNewMsg(e.target.value)} onKeyDown={handleKeyDown}
            placeholder="Scrivi un messaggio..." rows={1}
            className="flex-1 bg-[#F0F7FC] border border-[#B8D4E8] text-[#0D1B2A] px-3 py-2 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm resize-none" data-testid="chat-input" />
          <button onClick={() => sendMessage()} disabled={sending || !newMsg.trim()}
            className="px-4 py-2 bg-[#FF3B30] text-white hover:bg-[#FF1A1A] transition-all disabled:opacity-40" data-testid="chat-send-btn">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) sendPhotoFile(e.target.files[0]); e.target.value=''; }} />
        <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(e) => { if (e.target.files[0]) sendVideoFile(e.target.files[0]); e.target.value=''; }} />
        <p className="text-[8px] text-[#B8D4E8] mt-1 uppercase tracking-widest">
          {isOperator ? 'Fotocamera = richiedi foto | Video = richiedi video all\'utente' : 'Invio per inviare | Usa icone per foto e video'}
        </p>
      </div>
    </div>
  );
}
